/**
 * Pure utility functions following FP principles
 * All functions are pure - no side effects
 */
import type { Result, FilterFn } from '../types/common';

/**
 * Creates a success result
 */
export const ok = <T>(data: T): Result<T, never> => ({
  success: true,
  data,
});

/**
 * Creates an error result
 */
export const err = <E>(error: E): Result<never, E> => ({
  success: false,
  error,
});

/**
 * Maps a Result's success value
 */
export const mapResult = <T, R, E>(
  result: Result<T, E>,
  fn: (data: T) => R
): Result<R, E> => {
  if (result.success) {
    return ok(fn(result.data));
  }
  return result;
};

/**
 * Chains Result operations (flatMap/bind)
 */
export const chainResult = <T, R, E>(
  result: Result<T, E>,
  fn: (data: T) => Result<R, E>
): Result<R, E> => {
  if (result.success) {
    return fn(result.data);
  }
  return result;
};

/**
 * Pipe function for composing operations left-to-right
 */
export const pipe = <T>(...fns: Array<(arg: T) => T>) => (value: T): T =>
  fns.reduce((acc, fn) => fn(acc), value);

/**
 * Compose function for composing operations right-to-left
 */
export const compose = <T>(...fns: Array<(arg: T) => T>) => (value: T): T =>
  fns.reduceRight((acc, fn) => fn(acc), value);

/**
 * Creates a filter function from multiple predicates (AND logic)
 */
export const allOf = <T>(...predicates: Array<FilterFn<T>>): FilterFn<T> =>
  (item: T) => predicates.every(predicate => predicate(item));

/**
 * Creates a filter function from multiple predicates (OR logic)
 */
export const anyOf = <T>(...predicates: Array<FilterFn<T>>): FilterFn<T> =>
  (item: T) => predicates.some(predicate => predicate(item));

/**
 * Negates a filter function
 */
export const not = <T>(predicate: FilterFn<T>): FilterFn<T> =>
  (item: T) => !predicate(item);

/**
 * Safe array access that returns Option-like Result
 */
export const safeArrayAccess = <T>(
  arr: ReadonlyArray<T>,
  index: number
): Result<T, string> => {
  if (index >= 0 && index < arr.length) {
    return ok(arr[index]);
  }
  return err('Index out of bounds');
};

/**
 * Safe object property access
 */
export const safeProp = <T extends object, K extends keyof T>(
  obj: T,
  key: K
): Result<T[K], string> => {
  if (key in obj) {
    return ok(obj[key]);
  }
  return err(`Property ${String(key)} not found`);
};

/**
 * Immutable array update
 */
export const updateAt = <T>(
  arr: ReadonlyArray<T>,
  index: number,
  value: T
): ReadonlyArray<T> => [
  ...arr.slice(0, index),
  value,
  ...arr.slice(index + 1),
];

/**
 * Immutable array removal
 */
export const removeAt = <T>(
  arr: ReadonlyArray<T>,
  index: number
): ReadonlyArray<T> => [
  ...arr.slice(0, index),
  ...arr.slice(index + 1),
];

/**
 * Immutable object update
 */
export const updateObject = <T extends object>(
  obj: T,
  updates: Partial<T>
): T => ({
  ...obj,
  ...updates,
});

/**
 * Groups array items by a key function
 */
export const groupBy = <T, K extends string | number>(
  arr: ReadonlyArray<T>,
  keyFn: (item: T) => K
): Record<K, ReadonlyArray<T>> =>
  arr.reduce((acc, item) => {
    const key = keyFn(item);
    return {
      ...acc,
      [key]: [...(acc[key] || []), item],
    };
  }, {} as Record<K, ReadonlyArray<T>>);

/**
 * Removes duplicates from array using a key function
 */
export const uniqueBy = <T, K>(
  arr: ReadonlyArray<T>,
  keyFn: (item: T) => K
): ReadonlyArray<T> => {
  const seen = new Set<K>();
  return arr.filter(item => {
    const key = keyFn(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Sorts array by a key function (returns new array)
 */
export const sortBy = <T>(
  arr: ReadonlyArray<T>,
  keyFn: (item: T) => number | string,
  direction: 'asc' | 'desc' = 'asc'
): ReadonlyArray<T> => {
  const sorted = [...arr].sort((a, b) => {
    const aKey = keyFn(a);
    const bKey = keyFn(b);
    if (aKey < bKey) return direction === 'asc' ? -1 : 1;
    if (aKey > bKey) return direction === 'asc' ? 1 : -1;
    return 0;
  });
  return sorted;
};

/**
 * Delays execution (for async operations)
 */
export const delay = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retries an async operation
 */
export const retry = async <T>(
  fn: () => Promise<T>,
  maxAttempts: number = 3,
  delayMs: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      if (attempt < maxAttempts) {
        await delay(delayMs * attempt);
      }
    }
  }
  
  throw lastError!;
};

/**
 * Debounces a function
 */
export const debounce = <T extends (...args: any[]) => any>(
  fn: T,
  ms: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  };
};

/**
 * Throttles a function
 */
export const throttle = <T extends (...args: any[]) => any>(
  fn: T,
  ms: number
): ((...args: Parameters<T>) => void) => {
  let lastRun = 0;
  
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastRun >= ms) {
      fn(...args);
      lastRun = now;
    }
  };
};

/**
 * Memoizes a function
 */
export const memoize = <T extends (...args: any[]) => any>(
  fn: T
): T => {
  const cache = new Map<string, ReturnType<T>>();
  
  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key)!;
    }
    const result = fn(...args);
    cache.set(key, result);
    return result;
  }) as T;
};

/**
 * Formats duration in seconds to human-readable format
 */
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats a date to a relative time string
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffDay < 30) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString();
};

/**
 * Truncates text to a specified length
 */
export const truncate = (text: string, maxLength: number): string =>
  text.length <= maxLength ? text : `${text.slice(0, maxLength)}...`;

/**
 * Capitalizes first letter of each word
 */
export const titleCase = (text: string): string =>
  text.replace(/\b\w/g, char => char.toUpperCase());
